import React from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onUploadAsset?: (file: File, kind: 'image' | 'video') => Promise<string>;
}

const hasHtml = (value: string): boolean => /<[^>]+>/.test(value);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const toRichTextHtml = (value: string): string => {
  if (!value) return '';
  if (hasHtml(value)) return value;

  const paragraphs = value
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  if (!paragraphs.length) return '';

  return paragraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br/>')}</p>`)
    .join('');
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write content...',
  className = '',
  onUploadAsset,
}: RichTextEditorProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const videoInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = toRichTextHtml(value);
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [value]);

  const emitChange = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    onChange(el.innerHTML);
  }, [onChange]);

  const runCommand = (command: string, commandValue?: string) => {
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const insertHtml = (html: string) => {
    document.execCommand('insertHTML', false, html);
    emitChange();
  };

  const addLink = () => {
    const url = window.prompt('Enter URL');
    if (!url) return;
    runCommand('createLink', url);
  };

  const insertImageFromUrl = () => {
    const url = window.prompt('Enter image URL');
    if (!url) return;
    insertHtml(`<img src="${url}" alt="" style="max-width:100%;height:auto;" />`);
  };

  const insertVideoFromUrl = () => {
    const url = window.prompt('Enter video URL');
    if (!url) return;
    insertHtml(`<video controls src="${url}" style="max-width:100%;height:auto;"></video>`);
  };

  const handleAssetUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    kind: 'image' | 'video'
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!onUploadAsset) {
      window.alert('Upload is not configured for this editor.');
      return;
    }

    try {
      setIsUploading(true);
      const publicUrl = await onUploadAsset(file, kind);
      if (kind === 'image') {
        insertHtml(`<img src="${publicUrl}" alt="" style="max-width:100%;height:auto;" />`);
      } else {
        insertHtml(`<video controls src="${publicUrl}" style="max-width:100%;height:auto;"></video>`);
      }
    } catch (err: any) {
      console.error('Asset upload failed:', err);
      window.alert(err?.message || 'Failed to upload asset');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`border rounded bg-white ${className}`}>
      <div className="flex flex-wrap items-center gap-1 border-b bg-gray-50 px-2 py-2">
        <button type="button" onClick={() => runCommand('bold')} className="px-2 py-1 text-sm border rounded">B</button>
        <button type="button" onClick={() => runCommand('italic')} className="px-2 py-1 text-sm border rounded">I</button>
        <button type="button" onClick={() => runCommand('underline')} className="px-2 py-1 text-sm border rounded">U</button>
        <button
          type="button"
          onClick={() => runCommand('insertUnorderedList')}
          className="px-2 py-1 text-sm border rounded"
          title="Bulleted list items (<li>)"
        >
          {'<li>'}
        </button>
        <button
          type="button"
          onClick={() => runCommand('insertOrderedList')}
          className="px-2 py-1 text-sm border rounded"
          title="Numbered list (<ol>)"
        >
          {'<ol>'}
        </button>
        <button type="button" onClick={addLink} className="px-2 py-1 text-sm border rounded">Link</button>
        <button type="button" onClick={() => imageInputRef.current?.click()} className="px-2 py-1 text-sm border rounded" disabled={isUploading}>Upload Image</button>
        <button type="button" onClick={insertImageFromUrl} className="px-2 py-1 text-sm border rounded">Image URL</button>
        <button type="button" onClick={() => videoInputRef.current?.click()} className="px-2 py-1 text-sm border rounded" disabled={isUploading}>Upload Video</button>
        <button type="button" onClick={insertVideoFromUrl} className="px-2 py-1 text-sm border rounded">Video URL</button>
        <button type="button" onClick={() => runCommand('removeFormat')} className="px-2 py-1 text-sm border rounded">Clear</button>
        {isUploading && <span className="text-xs text-gray-500 ml-2">Uploading...</span>}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleAssetUpload(e, 'image')}
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => handleAssetUpload(e, 'video')}
        />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        data-placeholder={placeholder}
        className="min-h-[220px] px-3 py-2 prose prose-slate max-w-none focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
      />
    </div>
  );
}
